import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { HomePageContext } from '../HomePageContext';
import { connectContext } from '../../context/helpers/connectContext';
import { RenderIn, device } from '../../utils/RenderIn';
import {
    trackInitialDisplay,
    trackArrowClick,
    trackItemClick,
    trackCarouselItems,
} from '../helpers/carouselTracking';
import { getItemsPerPage } from '../helpers/getItemsPerPage';
import { HpRespCarouselTracking } from '../../homepage/HpRespCarouselTracking/HpRespCarouselTracking';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';

import styles from './HpRespCarousel.scss';

export const handleItemClick = ({ item, index, actionLabel }) => {
    trackItemClick({ actionLabel });
    trackCarouselItems({
        actionLabel,
        type: 'productClick',
        items: [item],
        startIndex: index + 1, // Tracking needs non zero index position.
        variant: 'homepage',
    });
};

export const trackImpressions = ({ visibleItems, index, actionLabel }) => {
    trackCarouselItems({
        actionLabel,
        type: 'productImpression',
        items: visibleItems,
        startIndex: index + 1, // Tracking needs non zero index position.
        variant: 'homepage',
    });
};

export const onInitialDisplay = ({ actionLabel, items }) => {
    const itemsReturned = items.length;
    trackInitialDisplay({ itemsReturned, actionLabel });
};

const HpRespCarouselComponent = props => {
    const {
        actionLabel,
        items,
        showDots,
        isAutoRun,
        isMobile,
        isTablet,
        itemsPerRow,
        children,
    } = props;
    const itemsPerPage = getItemsPerPage({ isMobile, isTablet, itemsPerRow });
    return (
        <HpRespCarouselTracking
            items={items}
            itemsPerPage={itemsPerPage}
            onItemsImpression={({ visibleItems, index }) =>
                trackImpressions({ visibleItems, index, actionLabel })
            }
            onInitialDisplay={() => onInitialDisplay({ actionLabel, items })}
        >
            {({ handlePageChange, handleIndexChange }) => (
                <Fragment>
                    <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                        <SharedInViewport>
                            {({ inViewport }) => (
                                <Carousel
                                    hideArrows={showDots}
                                    isAutoRun={isAutoRun ? inViewport : false}
                                    totalItems={children.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({ index }) => children[index]}
                                    onIndexChange={indexChangeProps =>
                                        handleIndexChange(indexChangeProps)
                                    }
                                    onLeftArrowClick={() =>
                                        trackArrowClick({
                                            direction: 'left',
                                            actionLabel,
                                        })
                                    }
                                    onRightArrowClick={() =>
                                        trackArrowClick({
                                            direction: 'right',
                                            actionLabel,
                                        })
                                    }
                                    classNames={{
                                        arrowRight: styles.arrowRight,
                                        arrowLeft: styles.arrowLeft,
                                        wrapper: styles.carouselWrapper,
                                        list: styles.carouselList,
                                    }}
                                    renderDot={({ isCurrentDot } = {}) => (
                                        <SharedCarouselDot isActive={isCurrentDot} />
                                    )}
                                    showDots={showDots}
                                />
                            )}
                        </SharedInViewport>
                    </RenderIn>
                    <RenderIn deviceTypes={[device.MOBILE]}>
                        <div className="primary-viewport-width">
                            <Swiper
                                itemsPerPage={itemsPerPage}
                                onPageChange={page => handlePageChange(page)}
                            >
                                {children}
                            </Swiper>
                        </div>
                    </RenderIn>
                </Fragment>
            )}
        </HpRespCarouselTracking>
    );
};

HpRespCarouselComponent.defaultProps = {
    showPrice: false,
    showDots: false,
    isAutoRun: false,
    children: [],
};

HpRespCarouselComponent.propTypes = {
    isAutoRun: PropTypes.bool.isRequired,
    actionLabel: PropTypes.string,
    isDesktop: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isTablet: PropTypes.bool.isRequired,
    items: PropTypes.array.isRequired,
    itemsPerRow: PropTypes.number,
    showPrice: PropTypes.bool.isRequired,
    showDots: PropTypes.bool.isRequired,
    children: PropTypes.array.isRequired,
};

function mapContextToProps({ isMobile, isTablet, isDesktop }) {
    return { isMobile, isTablet, isDesktop };
}

export const HpRespCarousel = connectContext(
    HomePageContext,
    mapContextToProps
)(HpRespCarouselComponent);
